import React, { useState, useEffect } from 'react';
import { ReviewStatus, Allergy } from '../types/Allergy';
import AllergyCard from './AllergyCard';
import './AllergyList.css';
import { useVimOSPatient } from "../context/useVimOSPatient";
import { fetchAllergiesByMRN } from '../services/allergyService';


const AllergyList: React.FC = () => {
  const [allergies, setAllergies] = useState<Allergy[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const patient = useVimOSPatient();

  // Fetch allergies when patient MRN is available (or all allergies if no patient context)
  useEffect(() => {
    const loadAllergies = async () => {
      setLoading(true);
      try {
        const mrn = patient?.identifiers?.mrn || '';
        if (!mrn) {
          console.warn('No patient MRN available. Fetching all allergies.');
        }
        const data = await fetchAllergiesByMRN(mrn);
        setAllergies(data);
      } catch (error) {
        console.error('Error loading allergies:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAllergies();
  }, [patient?.identifiers?.mrn]);


  const handleReview = (id: string, status: ReviewStatus) => {
    setAllergies(prev =>
      prev.map(allergy =>
        allergy.id === id ? { ...allergy, reviewStatus: status } : allergy
      )
    );
  };

  const pendingCount = allergies.filter(a => a.reviewStatus === 'pending').length;
  
  return (
    <div className="allergy-list-container">
      <div className="patient-banner">
        <div className="patient-info-group">
          <div className="patient-name">
            {patient?.demographics?.firstName && patient?.demographics?.lastName 
              ? `${patient.demographics.firstName} ${patient.demographics.lastName}`
              : 'All Patients'}
          </div>
          <div className="patient-meta">
            <div className="meta-item">
              <span>Mrn:</span> {patient?.identifiers?.mrn || 'All'}
            </div>
            <div className="meta-item">
              <span>DOB:</span> {patient?.demographics?.dateOfBirth || 'N/A'}
            </div>
            <div className="meta-item">
              <span>Gender:</span> {patient?.demographics?.gender || 'N/A'}
            </div>
          </div>
        </div>
        
        {pendingCount > 0 && (
          <div className="status-summary-compact">
            <span className="status-count">{pendingCount}</span>
            <span>Pending Reviews</span>
          </div>
        )}
      </div>

      <div className="instruction-text">
        Review detected allergies below. Expand rows for evidence details.
      </div>

      {loading ? (
        <div className="loading-message">Loading allergies...</div>
      ) : allergies.length === 0 ? (
        <div className="no-allergies-message">No allergy detections found for this patient.</div>
      ) : (
        <div className="allergy-list">
          {allergies.map((allergy) => (
            <AllergyCard 
              key={allergy.id} 
              allergy={allergy} 
              onReview={handleReview}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AllergyList;
